/**
 * Custom Swatches Integration
 * Comprehensive JavaScript integration for all custom swatch functionality
 * Handles product pages, collections, cart drawer, quick-add modals, and more
 * 
 * @version 1.0.0
 * @author Shopify Theme Developer
 */

(function(window, document) {
  'use strict';

  /**
   * SwatchIntegration Class
   * Centralized management for all swatch-related functionality
   */
  class SwatchIntegration {
    constructor(options = {}) {
      // Configuration
      this.config = {
        selectors: {
          swatchContainer: '.custom-swatch-container',
          swatchInput: '.custom-swatch-input',
          productCard: '.card--product',
          productForm: 'form[action*="/cart/add"]',
          quickAddModal: '.quick-add-modal',
          cartDrawer: '#cart-drawer',
          productMedia: '.product__media',
          priceContainer: '.price',
          availability: '.product-availability',
          variantPicker: 'variant-picker',
          productInfo: '.product__info',
          recentlyViewed: '.recently-viewed-products',
          searchResults: '.search-results',
          collectionGrid: '.collection-grid',
          ...options.selectors
        },
        events: {
          variantChange: 'variant:change',
          swatchClick: 'swatch:click',
          mediaUpdate: 'media:update',
          priceUpdate: 'price:update',
          cartUpdate: 'cart:update',
          quickViewOpen: 'quickview:open',
          ...options.events
        },
        performance: {
          debounceDelay: 300,
          throttleDelay: 100,
          lazyLoadOffset: 50,
          ...options.performance
        },
        debug: options.debug || false,
        ...options
      };

      // State management
      this.state = {
        products: new Map(),
        activeVariants: new Map(),
        loadedComponents: new Set(),
        observers: new Map(),
        eventListeners: new Map()
      };

      // Performance utilities
      this.debounce = this._createDebounce();
      this.throttle = this._createThrottle();

      // Initialize
      this.init();
    }

    /**
     * Initialize the SwatchIntegration
     */
    init() {
      this.log('Initializing SwatchIntegration');
      
      // Wait for DOM ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._initialize());
      } else {
        this._initialize();
      }
    }

    /**
     * Private initialization method
     */
    _initialize() {
      try {
        // Initialize existing swatches
        this.initializeSwatches();
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Setup mutation observers for dynamic content
        this.setupObservers();
        
        // Initialize performance optimizations
        this.initializePerformanceOptimizations();
        
        // Setup debugging if enabled
        if (this.config.debug) {
          this.setupDebugging();
        }
        
        this.log('SwatchIntegration initialized successfully');
        
        // Dispatch initialization complete event
        this.dispatchEvent('swatchintegration:ready');
        
      } catch (error) {
        this.error('Failed to initialize SwatchIntegration', error);
      }
    }

    /**
     * Initialize all swatches on the page
     */
    initializeSwatches(container = document) {
      this.log('Initializing swatches', container);
      
      const swatchContainers = container.querySelectorAll(this.config.selectors.swatchContainer);
      
      swatchContainers.forEach(swatchContainer => {
        this.initializeSwatchContainer(swatchContainer);
      });
    }

    /**
     * Initialize a single swatch container
     */
    initializeSwatchContainer(container) {
      if (!container || container.dataset.swatchInitialized) return;
      
      const productId = this.getProductId(container);
      const componentType = this.getComponentType(container);
      
      this.log(`Initializing swatch container for product ${productId} in ${componentType}`);
      
      // Store product data
      if (productId) {
        this.state.products.set(productId, {
          container,
          componentType,
          variants: this.extractVariantData(container),
          currentVariant: null
        });
      }
      
      // Setup swatch inputs
      const swatchInputs = container.querySelectorAll(this.config.selectors.swatchInput);
      swatchInputs.forEach(input => this.setupSwatchInput(input, productId));
      
      // Mark as initialized
      container.dataset.swatchInitialized = 'true';
      
      // Initialize first variant if needed
      this.initializeDefaultVariant(container, productId);
    }

    /**
     * Setup individual swatch input
     */
    setupSwatchInput(input, productId) {
      // Remove any existing listeners
      this.removeEventListener(input, 'change');
      this.removeEventListener(input, 'click');
      
      // Add new listeners
      const changeHandler = (e) => this.handleSwatchChange(e, productId);
      const clickHandler = (e) => this.handleSwatchClick(e, productId);
      
      this.addEventListener(input, 'change', changeHandler);
      this.addEventListener(input, 'click', clickHandler);
      
      // Add hover effects for better UX
      if (input.type === 'radio' || input.type === 'checkbox') {
        const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
        if (label) {
          this.addEventListener(label, 'mouseenter', () => this.handleSwatchHover(input, productId));
        }
      }
    }

    /**
     * Handle swatch change event
     */
    handleSwatchChange(event, productId) {
      event.stopPropagation();
      
      const input = event.target;
      const value = input.value;
      const optionName = input.dataset.optionName || input.name;
      
      this.log(`Swatch changed: Product ${productId}, Option ${optionName}, Value ${value}`);
      
      // Update variant selection
      this.updateVariantSelection(productId, optionName, value);
      
      // Dispatch custom event
      this.dispatchEvent(this.config.events.swatchClick, {
        productId,
        optionName,
        value,
        input
      });
    }

    /**
     * Handle swatch click event
     */
    handleSwatchClick(event, productId) {
      // Prevent default if needed
      if (event.target.tagName === 'A') {
        event.preventDefault();
      }
      
      // Ensure proper focus management
      if (event.target.type === 'radio' || event.target.type === 'checkbox') {
        event.target.focus();
      }
    }

    /**
     * Handle swatch hover for preview
     */
    handleSwatchHover(input, productId) {
      if (!this.config.enableHoverPreview) return;
      
      const value = input.value;
      const imageUrl = input.dataset.imageUrl;
      
      if (imageUrl) {
        this.preloadImage(imageUrl);
      }
    }

    /**
     * Update variant selection
     */
    updateVariantSelection(productId, optionName, value) {
      const productData = this.state.products.get(productId);
      if (!productData) return;
      
      // Get current selections
      const currentSelections = this.getCurrentSelections(productData.container);
      currentSelections[optionName] = value;
      
      // Find matching variant
      const variant = this.findVariant(productData.variants, currentSelections);
      
      if (variant) {
        this.log(`Found variant ${variant.id} for product ${productId}`);
        
        // Update active variant
        this.state.activeVariants.set(productId, variant);
        productData.currentVariant = variant;
        
        // Synchronize across all components
        this.synchronizeVariantSelection(productId, variant);
        
        // Update UI components
        this.updateUIComponents(productId, variant);
        
        // Dispatch variant change event
        this.dispatchEvent(this.config.events.variantChange, {
          productId,
          variant,
          previousVariant: productData.previousVariant
        });
        
        productData.previousVariant = variant;
      } else {
        this.log(`No variant found for selections`, currentSelections);
        this.handleUnavailableVariant(productId, currentSelections);
      }
    }

    /**
     * Synchronize variant selection across all components
     */
    synchronizeVariantSelection(productId, variant) {
      this.log(`Synchronizing variant ${variant.id} across components`);
      
      // Find all instances of this product
      const productInstances = this.findProductInstances(productId);
      
      productInstances.forEach(instance => {
        if (instance.dataset.skipSync) return;
        
        // Update swatch selections
        this.updateSwatchSelections(instance, variant);
        
        // Update hidden variant input if exists
        const variantInput = instance.querySelector('[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
        }
      });
    }

    /**
     * Update UI components based on variant selection
     */
    updateUIComponents(productId, variant) {
      const productData = this.state.products.get(productId);
      if (!productData) return;
      
      const container = productData.container;
      const componentType = productData.componentType;
      
      // Update based on component type
      switch (componentType) {
        case 'product-page':
          this.updateProductPage(container, variant);
          break;
        case 'product-card':
          this.updateProductCard(container, variant);
          break;
        case 'quick-view':
          this.updateQuickView(container, variant);
          break;
        case 'cart-item':
          this.updateCartItem(container, variant);
          break;
        default:
          this.updateGenericProduct(container, variant);
      }
      
      // Always update common elements
      this.updateCommonElements(container, variant);
    }

    /**
     * Update product page specific elements
     */
    updateProductPage(container, variant) {
      // Update main product image
      this.updateProductMedia(container, variant);
      
      // Update product information
      this.updateProductInfo(container, variant);
      
      // Update buy buttons
      this.updateBuyButtons(container, variant);
      
      // Update inventory status
      this.updateInventoryStatus(container, variant);
    }

    /**
     * Update product card specific elements
     */
    updateProductCard(container, variant) {
      const card = container.closest(this.config.selectors.productCard);
      if (!card) return;
      
      // Update card image
      const cardImage = card.querySelector('.card__media img');
      if (cardImage && variant.featured_image) {
        this.updateImage(cardImage, variant.featured_image);
      }
      
      // Update card price
      const cardPrice = card.querySelector('.price');
      if (cardPrice) {
        this.updatePrice(cardPrice, variant);
      }
      
      // Update card link
      const cardLink = card.querySelector('a[href*="/products/"]');
      if (cardLink && variant.url) {
        cardLink.href = variant.url;
      }
    }

    /**
     * Update product media/images
     */
    updateProductMedia(container, variant) {
      if (!variant.featured_image) return;
      
      const mediaContainer = container.querySelector(this.config.selectors.productMedia);
      if (!mediaContainer) return;
      
      // Debounced media update for performance
      this.debounce(() => {
        // Find and activate the correct media item
        const mediaItems = mediaContainer.querySelectorAll('[data-media-id]');
        mediaItems.forEach(item => {
          const mediaId = item.dataset.mediaId;
          if (mediaId === String(variant.featured_image.id)) {
            item.classList.add('is-active');
            // Scroll into view if needed
            if (item.scrollIntoView) {
              item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          } else {
            item.classList.remove('is-active');
          }
        });
        
        // Update main image if structure is different
        const mainImage = mediaContainer.querySelector('.product__main-image img');
        if (mainImage) {
          this.updateImage(mainImage, variant.featured_image);
        }
        
        // Dispatch media update event
        this.dispatchEvent(this.config.events.mediaUpdate, {
          variant,
          mediaContainer
        });
      }, 'media-update', this.config.performance.debounceDelay);
    }

    /**
     * Update product price
     */
    updatePrice(priceContainer, variant) {
      if (!priceContainer) return;
      
      // Format prices
      const formattedPrice = this.formatMoney(variant.price);
      const formattedComparePrice = variant.compare_at_price ? 
        this.formatMoney(variant.compare_at_price) : null;
      
      // Update regular price
      const regularPrice = priceContainer.querySelector('.price__regular .price-item--regular');
      if (regularPrice) {
        regularPrice.textContent = formattedPrice;
      }
      
      // Update sale price structure
      const salePrice = priceContainer.querySelector('.price__sale');
      if (variant.compare_at_price > variant.price) {
        // Show sale pricing
        if (salePrice) {
          salePrice.style.display = '';
          const salePriceRegular = salePrice.querySelector('.price-item--regular');
          const salePriceSale = salePrice.querySelector('.price-item--sale');
          
          if (salePriceRegular) salePriceRegular.textContent = formattedComparePrice;
          if (salePriceSale) salePriceSale.textContent = formattedPrice;
        }
        
        // Hide regular price
        const regularPriceContainer = priceContainer.querySelector('.price__regular');
        if (regularPriceContainer) {
          regularPriceContainer.style.display = 'none';
        }
        
        // Add on-sale class
        priceContainer.classList.add('price--on-sale');
      } else {
        // Hide sale pricing
        if (salePrice) {
          salePrice.style.display = 'none';
        }
        
        // Show regular price
        const regularPriceContainer = priceContainer.querySelector('.price__regular');
        if (regularPriceContainer) {
          regularPriceContainer.style.display = '';
        }
        
        // Remove on-sale class
        priceContainer.classList.remove('price--on-sale');
      }
      
      // Update unit price if applicable
      this.updateUnitPrice(priceContainer, variant);
      
      // Dispatch price update event
      this.dispatchEvent(this.config.events.priceUpdate, {
        variant,
        priceContainer
      });
    }

    /**
     * Update buy buttons based on variant availability
     */
    updateBuyButtons(container, variant) {
      const addButton = container.querySelector('[name="add"]');
      const buyNowButton = container.querySelector('[data-buy-now]');
      const quantity = container.querySelector('[name="quantity"]');
      
      if (variant.available) {
        // Enable buttons
        if (addButton) {
          addButton.disabled = false;
          addButton.textContent = addButton.dataset.addToCartText || 'Add to cart';
        }
        if (buyNowButton) {
          buyNowButton.disabled = false;
        }
        if (quantity) {
          quantity.disabled = false;
          quantity.max = variant.inventory_quantity > 0 ? variant.inventory_quantity : '';
        }
      } else {
        // Disable buttons
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = addButton.dataset.soldOutText || 'Sold out';
        }
        if (buyNowButton) {
          buyNowButton.disabled = true;
        }
        if (quantity) {
          quantity.disabled = true;
        }
      }
    }

    /**
     * Handle dynamic content (AJAX-loaded products)
     */
    handleDynamicContent(container) {
      this.log('Handling dynamic content', container);
      
      // Re-initialize swatches in the new content
      this.initializeSwatches(container);
      
      // Setup any additional event listeners
      this.setupDynamicEventListeners(container);
      
      // Handle specific dynamic content types
      if (container.matches(this.config.selectors.quickAddModal)) {
        this.handleQuickAddModal(container);
      } else if (container.matches(this.config.selectors.cartDrawer)) {
        this.handleCartDrawer(container);
      } else if (container.matches(this.config.selectors.searchResults)) {
        this.handleSearchResults(container);
      }
    }

    /**
     * Handle quick-add modal
     */
    handleQuickAddModal(modal) {
      this.log('Handling quick-add modal');
      
      // Find product form in modal
      const form = modal.querySelector(this.config.selectors.productForm);
      if (!form) return;
      
      // Get product ID from form
      const productId = form.dataset.productId;
      if (!productId) return;
      
      // Initialize swatches specific to quick-add
      this.initializeSwatches(modal);
      
      // Setup form submission handler
      this.setupQuickAddForm(form, productId);
      
      // Sync with main product if on product page
      this.syncWithMainProduct(productId);
    }

    /**
     * Setup quick-add form
     */
    setupQuickAddForm(form, productId) {
      const submitHandler = (e) => {
        e.preventDefault();
        
        const variant = this.state.activeVariants.get(productId);
        if (!variant) {
          this.error('No variant selected for quick-add');
          return;
        }
        
        // Add to cart via AJAX
        this.addToCart(variant.id, form);
      };
      
      this.removeEventListener(form, 'submit');
      this.addEventListener(form, 'submit', submitHandler);
    }

    /**
     * Add variant to cart
     */
    async addToCart(variantId, form) {
      const quantity = form.querySelector('[name="quantity"]')?.value || 1;
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: parseInt(quantity)
          })
        });
        
        if (!response.ok) throw new Error('Failed to add to cart');
        
        const data = await response.json();
        
        // Update cart UI
        this.updateCart(data);
        
        // Dispatch cart update event
        this.dispatchEvent(this.config.events.cartUpdate, { item: data });
        
        // Show success notification
        this.showNotification('Product added to cart successfully');
        
      } catch (error) {
        this.error('Failed to add to cart', error);
        this.showNotification('Failed to add product to cart', 'error');
      }
    }

    /**
     * Setup mutation observers for dynamic content
     */
    setupObservers() {
      // Observer for body to catch all dynamic content
      const bodyObserver = new MutationObserver((mutations) => {
        this.throttle(() => {
          mutations.forEach(mutation => {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1) { // Element node
                // Check if it contains swatches
                if (node.querySelector && node.querySelector(this.config.selectors.swatchContainer)) {
                  this.handleDynamicContent(node);
                }
              }
            });
          });
        }, 'mutation-observer', this.config.performance.throttleDelay);
      });
      
      bodyObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      this.state.observers.set('body', bodyObserver);
    }

    /**
     * Setup global event listeners
     */
    setupEventListeners() {
      // Listen for Shopify events
      document.addEventListener('shopify:section:load', (e) => {
        this.handleSectionLoad(e.detail.sectionId);
      });
      
      document.addEventListener('product:variant-change', (e) => {
        this.handleExternalVariantChange(e.detail);
      });
      
      // Listen for cart updates
      document.addEventListener('cart:updated', (e) => {
        this.handleCartUpdate(e.detail);
      });
      
      // Listen for theme editor events
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (e) => {
          this.refresh(e.target);
        });
      }
    }

    /**
     * Performance Optimizations
     */
    initializePerformanceOptimizations() {
      // Lazy load images
      if ('IntersectionObserver' in window) {
        this.setupLazyLoading();
      }
      
      // Preload critical images
      this.preloadCriticalImages();
      
      // Setup request idle callback for non-critical tasks
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.performNonCriticalInitialization();
        });
      }
    }

    /**
     * Setup lazy loading for swatch images
     */
    setupLazyLoading() {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          }
        });
      }, {
        rootMargin: `${this.config.performance.lazyLoadOffset}px`
      });
      
      // Observe all lazy images
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
      
      this.state.observers.set('images', imageObserver);
    }

    /**
     * Debugging capabilities
     */
    setupDebugging() {
      // Add to window for console access
      window.SwatchIntegration = this;
      
      // Add debug panel
      this.createDebugPanel();
      
      // Log all events
      this.logAllEvents();
      
      console.log('%c SwatchIntegration Debug Mode Enabled ', 
        'background: #222; color: #bada55; font-size: 14px; padding: 5px;');
      console.log('Access SwatchIntegration via: window.SwatchIntegration');
      console.log('Available methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(this)));
    }

    /**
     * Create debug panel
     */
    createDebugPanel() {
      const panel = document.createElement('div');
      panel.id = 'swatch-debug-panel';
      panel.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 15px;
        border-radius: 5px;
        font-family: monospace;
        font-size: 12px;
        max-width: 300px;
        max-height: 400px;
        overflow-y: auto;
        z-index: 99999;
        display: none;
      `;
      
      panel.innerHTML = `
        <div style="margin-bottom: 10px;">
          <strong>Swatch Integration Debug</strong>
          <button id="close-debug" style="float: right; background: red; color: white; border: none; padding: 2px 8px; cursor: pointer;">×</button>
        </div>
        <div id="debug-content"></div>
      `;
      
      document.body.appendChild(panel);
      
      // Toggle with keyboard shortcut (Ctrl+Shift+D)
      document.addEventListener('keydown', (e) => {
        if (e.ctrlKey && e.shiftKey && e.key === 'D') {
          panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
        }
      });
      
      // Close button
      document.getElementById('close-debug').addEventListener('click', () => {
        panel.style.display = 'none';
      });
      
      this.debugPanel = panel;
      this.debugContent = document.getElementById('debug-content');
    }

    /**
     * Update debug panel
     */
    updateDebugPanel(message, data = null) {
      if (!this.debugContent) return;
      
      const entry = document.createElement('div');
      entry.style.cssText = 'margin: 5px 0; padding: 5px; background: rgba(255,255,255,0.1);';
      
      const timestamp = new Date().toLocaleTimeString();
      entry.innerHTML = `
        <div><small>${timestamp}</small></div>
        <div>${message}</div>
        ${data ? `<pre style="margin: 5px 0; font-size: 10px;">${JSON.stringify(data, null, 2)}</pre>` : ''}
      `;
      
      this.debugContent.insertBefore(entry, this.debugContent.firstChild);
      
      // Keep only last 20 entries
      while (this.debugContent.children.length > 20) {
        this.debugContent.removeChild(this.debugContent.lastChild);
      }
    }

    /**
     * Utility Methods
     */
    
    /**
     * Get product ID from container
     */
    getProductId(container) {
      return container.dataset.productId || 
             container.closest('[data-product-id]')?.dataset.productId ||
             container.querySelector('[data-product-id]')?.dataset.productId;
    }

    /**
     * Get component type from container
     */
    getComponentType(container) {
      if (container.closest('.product__info')) return 'product-page';
      if (container.closest(this.config.selectors.productCard)) return 'product-card';
      if (container.closest(this.config.selectors.quickAddModal)) return 'quick-view';
      if (container.closest(this.config.selectors.cartDrawer)) return 'cart-item';
      if (container.closest(this.config.selectors.recentlyViewed)) return 'recently-viewed';
      if (container.closest(this.config.selectors.searchResults)) return 'search-result';
      return 'generic';
    }

    /**
     * Extract variant data from container
     */
    extractVariantData(container) {
      const scriptTag = container.querySelector('script[type="application/json"]');
      if (scriptTag) {
        try {
          const data = JSON.parse(scriptTag.textContent);
          return data.variants || [];
        } catch (e) {
          this.error('Failed to parse variant data', e);
        }
      }
      return [];
    }

    /**
     * Get current selections from container
     */
    getCurrentSelections(container) {
      const selections = {};
      const inputs = container.querySelectorAll(`${this.config.selectors.swatchInput}:checked`);
      
      inputs.forEach(input => {
        const optionName = input.dataset.optionName || input.name;
        selections[optionName] = input.value;
      });
      
      return selections;
    }

    /**
     * Find variant by selections
     */
    findVariant(variants, selections) {
      return variants.find(variant => {
        return Object.keys(selections).every(key => {
          const optionIndex = parseInt(key.replace(/\D/g, '')) - 1;
          return variant.options[optionIndex] === selections[key];
        });
      });
    }

    /**
     * Find all instances of a product on the page
     */
    findProductInstances(productId) {
      return document.querySelectorAll(`[data-product-id="${productId}"]`);
    }

    /**
     * Update swatch selections in a container
     */
    updateSwatchSelections(container, variant) {
      variant.options.forEach((option, index) => {
        const input = container.querySelector(
          `${this.config.selectors.swatchInput}[value="${option}"][data-option-position="${index + 1}"]`
        );
        
        if (input) {
          input.checked = true;
          // Trigger change event for any listeners
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    /**
     * Update image element
     */
    updateImage(img, imageData) {
      const src = this.getSizedImageUrl(imageData.src || imageData, 'large');
      
      if (img.src !== src) {
        // Add loading class
        img.classList.add('is-loading');
        
        // Preload image
        const tempImg = new Image();
        tempImg.onload = () => {
          img.src = src;
          img.srcset = this.generateSrcset(imageData.src || imageData);
          img.classList.remove('is-loading');
        };
        tempImg.src = src;
      }
    }

    /**
     * Preload image
     */
    preloadImage(url) {
      const img = new Image();
      img.src = url;
    }

    /**
     * Format money
     */
    formatMoney(cents) {
      const money = (cents / 100).toFixed(2);
      return window.Shopify?.formatMoney ? 
        window.Shopify.formatMoney(cents) : 
        `$${money}`;
    }

    /**
     * Get sized image URL
     */
    getSizedImageUrl(src, size = 'large') {
      if (!src) return '';
      
      const sizeMap = {
        'small': '150x150',
        'medium': '300x300',
        'large': '600x600',
        'xlarge': '1200x1200'
      };
      
      const sizeParam = sizeMap[size] || size;
      return src.replace(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i, `_${sizeParam}.$1$2`);
    }

    /**
     * Generate srcset for responsive images
     */
    generateSrcset(src) {
      const sizes = ['300', '600', '900', '1200'];
      return sizes.map(size => {
        return `${this.getSizedImageUrl(src, `${size}x${size}`)} ${size}w`;
      }).join(', ');
    }

    /**
     * Create debounce function
     */
    _createDebounce() {
      const timers = {};
      return (func, key, delay) => {
        clearTimeout(timers[key]);
        timers[key] = setTimeout(func, delay);
      };
    }

    /**
     * Create throttle function
     */
    _createThrottle() {
      const timers = {};
      return (func, key, delay) => {
        if (!timers[key]) {
          func();
          timers[key] = setTimeout(() => {
            delete timers[key];
          }, delay);
        }
      };
    }

    /**
     * Dispatch custom event
     */
    dispatchEvent(eventType, detail = {}) {
      const event = new CustomEvent(eventType, {
        detail,
        bubbles: true,
        cancelable: true
      });
      
      document.dispatchEvent(event);
      
      if (this.config.debug) {
        this.log(`Event dispatched: ${eventType}`, detail);
        this.updateDebugPanel(`Event: ${eventType}`, detail);
      }
    }

    /**
     * Add event listener with tracking
     */
    addEventListener(element, event, handler) {
      element.addEventListener(event, handler);
      
      // Track for cleanup
      if (!this.state.eventListeners.has(element)) {
        this.state.eventListeners.set(element, new Map());
      }
      this.state.eventListeners.get(element).set(event, handler);
    }

    /**
     * Remove event listener
     */
    removeEventListener(element, event) {
      const listeners = this.state.eventListeners.get(element);
      if (listeners) {
        const handler = listeners.get(event);
        if (handler) {
          element.removeEventListener(event, handler);
          listeners.delete(event);
        }
      }
    }

    /**
     * Show notification
     */
    showNotification(message, type = 'success') {
      // Use theme's notification system if available
      if (window.theme?.showNotification) {
        window.theme.showNotification(message, type);
      } else {
        // Fallback to console
        console[type === 'error' ? 'error' : 'log'](message);
      }
    }

    /**
     * Logging utilities
     */
    log(...args) {
      if (this.config.debug) {
        console.log('[SwatchIntegration]', ...args);
      }
    }

    warn(...args) {
      if (this.config.debug) {
        console.warn('[SwatchIntegration]', ...args);
      }
    }

    error(...args) {
      console.error('[SwatchIntegration]', ...args);
    }

    /**
     * Log all events for debugging
     */
    logAllEvents() {
      Object.values(this.config.events).forEach(eventType => {
        document.addEventListener(eventType, (e) => {
          this.log(`Event captured: ${eventType}`, e.detail);
        });
      });
    }

    /**
     * Public API Methods
     */
    
    /**
     * Refresh swatches in a container
     */
    refresh(container = document) {
      this.log('Refreshing swatches', container);
      
      // Remove initialized flag
      const swatchContainers = container.querySelectorAll('[data-swatch-initialized]');
      swatchContainers.forEach(c => delete c.dataset.swatchInitialized);
      
      // Re-initialize
      this.initializeSwatches(container);
    }

    /**
     * Get active variant for a product
     */
    getActiveVariant(productId) {
      return this.state.activeVariants.get(productId);
    }

    /**
     * Set active variant for a product
     */
    setActiveVariant(productId, variantId) {
      const productData = this.state.products.get(productId);
      if (!productData) return false;
      
      const variant = productData.variants.find(v => v.id === variantId);
      if (!variant) return false;
      
      this.state.activeVariants.set(productId, variant);
      this.synchronizeVariantSelection(productId, variant);
      this.updateUIComponents(productId, variant);
      
      return true;
    }

    /**
     * Destroy the integration (cleanup)
     */
    destroy() {
      this.log('Destroying SwatchIntegration');
      
      // Remove all event listeners
      this.state.eventListeners.forEach((events, element) => {
        events.forEach((handler, event) => {
          element.removeEventListener(event, handler);
        });
      });
      this.state.eventListeners.clear();
      
      // Disconnect observers
      this.state.observers.forEach(observer => observer.disconnect());
      this.state.observers.clear();
      
      // Clear state
      this.state.products.clear();
      this.state.activeVariants.clear();
      this.state.loadedComponents.clear();
      
      // Remove debug panel if exists
      if (this.debugPanel) {
        this.debugPanel.remove();
      }
      
      // Remove from window
      if (window.SwatchIntegration === this) {
        delete window.SwatchIntegration;
      }
    }

    /**
     * Get integration stats for debugging
     */
    getStats() {
      return {
        products: this.state.products.size,
        activeVariants: this.state.activeVariants.size,
        loadedComponents: this.state.loadedComponents.size,
        observers: this.state.observers.size,
        eventListeners: this.state.eventListeners.size,
        config: this.config
      };
    }
  }

  /**
   * Auto-initialize on DOM ready
   */
  let swatchIntegrationInstance = null;

  function initializeSwatchIntegration(options = {}) {
    if (swatchIntegrationInstance) {
      swatchIntegrationInstance.destroy();
    }
    
    swatchIntegrationInstance = new SwatchIntegration(options);
    return swatchIntegrationInstance;
  }

  // Auto-initialize with default options
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeSwatchIntegration();
    });
  } else {
    initializeSwatchIntegration();
  }

  // Export for use in other scripts
  window.SwatchIntegration = SwatchIntegration;
  window.initializeSwatchIntegration = initializeSwatchIntegration;

})(window, document);